/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as MsDyn365Lib from '@msdyn365-commerce/core';
import MsDyn365 from '@msdyn365-commerce/core';
import { ICookieSetOptions, IImageDimension } from '@msdyn365-commerce/core-internal';
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICountryPickerAllData } from './country-picker-all.data';
import { IActionLinkData, ICountryListData, ICountryPickerAllProps } from './country-picker-all.props.autogenerated';

/**
 * CountryPicker view props interface.
 */
export interface ICountryPickerViewProps extends ICountryPickerAllProps<ICountryPickerAllData> {
    modalContainer: IModuleProps;
    modalHeaderContainer: INodeProps;
    modalFooterContainer: INodeProps;
    modalBodyContainer: INodeProps;
    heading?: React.ReactNode;
    subheading?: React.ReactNode;
    link?: React.ReactNode;
    countryList?: ICountryStructure[];
    isModalOpen?: boolean;
    state?: ICountryPickerState;
    stayOnCurrentSiteButton?: React.ReactNode;
}

export interface ICountryStructure {
    name?: string;
    elements?: React.ReactNode[];
}

/**
 * ICountryPickerState: Interface for Country picker State.
 */
export interface ICountryPickerState {
    isModalOpen: boolean;
}

/**
 *
 * CountryPicker component.
 * @extends {React.PureComponent<ICountryPickerAllProps<ICountryPickerAllData>>}
 */
export class CountryPicker extends React.PureComponent<ICountryPickerAllProps<ICountryPickerAllData>, ICountryPickerState> {
    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    private readonly _preferredSiteStorageKey: string = '_msdyn365___site_';
    private readonly _preferredSiteNameStorageKey: string = '_msdyn365___sitename_';

    //private readonly _cookieExpirationTime = 5_184_000;

    private readonly _makeBaseUrl = function(origin: string, path: string) {
        let ext = '';
        const base = origin.charAt(origin.length - 1) === '/' ? origin.slice(0, origin.length - 1) : origin;
        if (path.indexOf('/eu-en') === 0) {
            ext = 'eu-en';
        } else if (path.indexOf('/en') === 0) {
            ext = 'en';
        } else if (path.indexOf('/fr-ch') === 0) {
            ext = 'fr-ch';
        } else if (path.indexOf('/fr') === 0) {
            ext = 'fr';
        } else if (path.indexOf('/eu') === 0) {
            ext = 'eu';
        } else {
            return base;
        }
        return `${base}/${ext}`;
    };

    private validateCookieValue(cookie: string): boolean {
        if (
            cookie === `${this.props.context.request.url.requestUrl.origin}` ||
            cookie === `${this.props.context.request.url.requestUrl.origin}/` ||
            cookie === `${this.props.context.request.url.requestUrl.origin}/en` ||
            cookie === `${this.props.context.request.url.requestUrl.origin}/fr-ch` ||
            cookie === `${this.props.context.request.url.requestUrl.origin}/fr` ||
            cookie === `${this.props.context.request.url.requestUrl.origin}/eu` ||
            cookie === `${this.props.context.request.url.requestUrl.origin}/eu-en`
        ) {
            return true;
        }
        return false;
    }

    public constructor(props: ICountryPickerAllProps<ICountryPickerAllData>) {
        super(props);
        let isCookieValueSet: boolean = false;
        const baseUrl = this._makeBaseUrl(props.context.request.url.requestUrl.origin, props.context.request.url.requestUrl.pathname);
        let cookieValue: string | undefined;
        if (props.context.request.cookies.get(this._preferredSiteStorageKey).value) {
            isCookieValueSet = true;
            cookieValue = this._stripTrailingSlashes(props.context.request.cookies.get(this._preferredSiteStorageKey).value as string);
            if (MsDyn365.isBrowser) {
                if (
                    this.validateCookieValue(this._appendProtocolToUrl(cookieValue)) &&
                    this._appendProtocolToUrl(cookieValue) !== baseUrl &&
                    props.config.autoRedirect &&
                    window.location.href.search('localhost:4000') !== 8 //This will always be 8 if you are on localhost. please do not redirect
                ) {
                    window.location.replace(
                        this._appendProtocolToUrl(cookieValue) +
                            this.stripLocale(this.props.context.request.url.requestUrl.pathname.toString()) +
                            this.props.context.request.url.requestUrl.search ?? ''
                    );
                }
            }
        }
        if (this.props.config.autoRedirect) {
            this.state = {
                isModalOpen: !isCookieValueSet // || (cookieValue !== undefined && this._appendProtocolToUrl(cookieValue) !== baseUrl)
            };
        } else {
            this.state = {
                isModalOpen: !isCookieValueSet || (cookieValue !== undefined && this._appendProtocolToUrl(cookieValue) !== baseUrl)
            };
        }
    }

    public render(): JSX.Element | null {
        const { heading, subheading, countryList, actionLink } = this.props.config;

        const isConsentGiven = this.props.context.request.cookies.isConsentGiven();

        if (!isConsentGiven) {
            return null;
        }

        const countryPickerHeading = heading && (
            <MsDyn365Lib.Text
                className='ms-country-picker__heading'
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const countryPickerSubHeading = subheading && (
            <MsDyn365Lib.RichTextComponent
                text={subheading}
                className='ms-country-picker__subheading'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );

        const countryPickerActionLink = actionLink && this.createLink(actionLink);
        const currentSiteButton = (
            <Button className='ms-country-picker__current-site' onClick={this._handleStayOnSiteButtonClick}>
                {this.props.resources.stayOnCurrentSiteButtonText}
            </Button>
        );

        const viewprops: ICountryPickerViewProps = {
            ...this.props,
            state: this.state,
            isModalOpen: this.state.isModalOpen,
            modalContainer: {
                moduleProps: this.props,
                tag: Modal,
                className: 'ms-country-picker__dialog',
                isOpen: true,
                'aria-label': 'country-picker',
                toggle: this.closeModal,
                modalTransition: { timeout: 0 },
                shouldUpdateFocusToFirstElement: true
            },
            modalHeaderContainer: {
                tag: ModalHeader,
                className: 'ms-country-picker__modal-header',
                toggle: this.closeModal
            },
            modalFooterContainer: {
                tag: ModalFooter,
                className: 'ms-countryPicker__modal-footer'
            },
            modalBodyContainer: {
                tag: ModalBody,
                className: 'ms-counrtyPicker__modal-body'
            },
            heading: countryPickerHeading,
            subheading: countryPickerSubHeading,
            link: countryPickerActionLink,
            countryList: countryList && this.renderCountryList(),
            stayOnCurrentSiteButton: currentSiteButton
        };

        return this.props.renderView(viewprops);
    }

    /**
     * Initiate close modal.
     */
    public readonly closeModal = (): void => {
        this.setState({
            isModalOpen: false
        });
    };

    public handleParagraphChange(event: MsDyn365Lib.ContentEditableEvent): void {
        this.props.config.subheading = event.target.value;
    }

    public handleTextChange(event: MsDyn365Lib.ContentEditableEvent): void {
        this.props.config.heading!.text = event.target.value;
    }

    public handleLinkTextChange(event: MsDyn365Lib.ContentEditableEvent): void {
        if (this.props.config.actionLink?.linkText) {
            this.props.config.actionLink.linkText = event.target.value;
        }
    }

    public createLink(link?: IActionLinkData): React.ReactNode | null {
        if (!StringExtensions.isNullOrEmpty(link?.linkUrl.destinationUrl)) {
            const linkText = link?.linkText ?? '';
            const payLoad = getPayloadObject('click', this._telemetryContent, '', '');
            const attributes = getTelemetryAttributes(this._telemetryContent, payLoad);
            const editableLink: MsDyn365Lib.ILinksData = {
                ariaLabel: link?.ariaLabel,
                className: 'ms-countryPicker__link',
                linkText,
                linkUrl: link?.linkUrl.destinationUrl,
                openInNewTab: link?.openInNewTab,
                role: 'link',
                additionalProperties: attributes!,
                onClick: onTelemetryClick(this._telemetryContent, payLoad, linkText)
            };
            return (
                <MsDyn365Lib.Link
                    link={editableLink}
                    editProps={{ onTextChange: this.handleLinkTextChange, requestContext: this.props.context.request }}
                />
            );
        }
        return null;
    }

    public renderCountryList(): ICountryStructure[] {
        const countryList = this.props.config.countryList;

        const countryGroups = countryList && groupBy(countryList, x => x.displayGroup);

        const countryLinks: ICountryStructure[] = [];

        countryGroups?.forEach(countrydata => {
            const clinks = countrydata.map((item: ICountryListData) => {
                return this.renderCountry(item);
            });

            countryLinks.push({ name: countrydata[0].displayGroup, elements: clinks });
        });
        return countryLinks;
    }

    public renderCountry(item: ICountryListData): React.ReactNode {
        const imageSize: IImageDimension = { q: 'w=48&h=48&m=6', w: 0, h: 0 };
        const defaultImageSettings: MsDyn365Lib.IImageSettings = {
            viewports: {
                xs: imageSize,
                lg: imageSize,
                xl: imageSize
            },
            lazyload: true
        };
        const image = item.image;
        const imageProps = {
            gridSettings: this.props.context.request.gridSettings!,
            imageSettings: image?.imageSettings ?? defaultImageSettings
        };

        return (
            <a
                key={item.displayString}
                className={'ms-country-picker__country__item__link'}
                tabIndex={0}
                href={
                    item.url
                        ? this._stripTrailingSlashes(this._appendProtocolToUrl(item.url)) +
                              this.stripLocale(this.props.context.request.url.requestUrl.pathname.toString()) +
                              this.props.context.request.url.requestUrl.search ?? ''
                        : item.url +
                              this.stripLocale(this.props.context.request.url.requestUrl.pathname.toString()) +
                              this.props.context.request.url.requestUrl.search ?? ''
                }
                aria-label={item.displayString}
                onClick={this._handleClick(
                    this._preferredSiteStorageKey,
                    item.url ? this._appendProtocolToUrl(item.url) : item.url,
                    this._preferredSiteNameStorageKey,
                    `${item.displayGroup} (${item.displayString})`
                )}
            >
                {image && (
                    <MsDyn365Lib.Image
                        requestContext={this.props.context.request}
                        className='ms-country-picker__country__item__img'
                        key={item.displayString}
                        src={image.src ?? ''}
                        gridSettings={imageProps.gridSettings}
                        imageSettings={imageProps.imageSettings}
                        altText={image.altText}
                        loadFailureBehavior='hide'
                        shouldSkipToMainImage
                    />
                )}
                <div className='ms-country-picker__country__item__display'>
                    <span className='ms-country-picker__country__item__displayString'>{`${item.displayGroupLocalizedName ??
                        item.displayGroup}(${item.displayString})`}</span>
                </div>
            </a>
        );
    }

    private stripLocale(input: string): string {
        var result = input;
        if (input.includes('/eu-en') || input.includes('/fr-ch')) {
            result = input.slice(6);
        } else if (input.includes('/en') || input.includes('/eu') || input.includes('/fr')) {
            result = input.slice(3);
        } else if (input.length > 0) {
            result = input;
        }
        return result;
    }

    /**
     * Writes the current site base url to cookie and closes the country picker modal dialog.
     */
    private readonly _handleStayOnSiteButtonClick = (): void => {
        const currentBaseUrl = this.props.context.request.marketSettings?.currentBaseUrl;
        if (currentBaseUrl) {
            const urlToStore = this._appendProtocolToUrl(currentBaseUrl);
            this._writeToCookie(this._preferredSiteStorageKey, urlToStore);
        }
        this.closeModal();
    };

    /**
     * Handles click event of country selection.
     * @param storageKey - Cookie storage key.
     * @param storageValue - Preffered site url.
     * @returns - This method just writes the cookie to the browser.
     */
    private readonly _handleClick = (storageKey?: string, storageValue?: string, storageKey2?: string, storageValue2?: string) => () => {
        this._writeToCookie(storageKey, storageValue);
        this._writeToCookie(storageKey2, storageValue2);
    };

    /**
     * Handles click event of country selection.
     * @param storageKey - Cookie storage key.
     * @param storageValue - Preffered site url.
     */
    private readonly _writeToCookie = (storageKey?: string, storageValue?: string): void => {
        if (!storageKey || !storageValue) {
            return;
        }
        const cookieOptions: ICookieSetOptions = { secure: true, sameSite: 'none' };
        cookieOptions.maxAge = this.props.config.cookieLifetime;
        this.props.context.request.cookies.set(storageKey, storageValue, cookieOptions);
    };

    /**
     * Appends protocol to an url.
     * @param baseUrl - Url to be formatted.
     * @returns - URL after appending protocol.
     */
    private readonly _appendProtocolToUrl = (baseUrl: string): string => {
        return !baseUrl.includes('https://') ? `https://${baseUrl}` : baseUrl;
    };

    private readonly _stripTrailingSlashes = (path: string): string => {
        return path.replace(/\/+$/, '');
    };
}

function groupBy<K, V>(array: V[], grouper: (item: V) => K) {
    return array.reduce((store, item) => {
        var key = grouper(item);
        if (!store.has(key)) {
            store.set(key, [item]);
        } else {
            store?.get(key)?.push(item);
        }
        return store;
    }, new Map<K, V[]>());
}

export default CountryPicker;
